import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthProvider';
import { FaSpinner, FaEdit, FaTrash, FaPlusCircle, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

// ===================================================================================
// A. Table Skeleton Loader
// ===================================================================================
const TableSkeleton = () => (
    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
        <div className="animate-pulse">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center space-x-6 p-4 border-b border-gray-700/50 last:border-b-0">
                    <div className="h-4 bg-gray-700 rounded w-1/4"></div>
                    <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                    <div className="flex-1 flex justify-end gap-4">
                        <div className="h-8 w-8 bg-gray-700 rounded-md"></div>
                        <div className="h-8 w-8 bg-gray-700 rounded-md"></div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// ===================================================================================
// B. Main AI Training Component
// ===================================================================================
export function AITraining() {
    const [entries, setEntries] = useState([]);
    const [intent, setIntent] = useState('');
    const [baseResponse, setBaseResponse] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);
    const [feedback, setFeedback] = useState({ message: '', type: '' });
    const [editMode, setEditMode] = useState(false);

    const { auth } = useAuth();
    const formRef = useRef(null);

    const fetchEntries = async () => {
        try {
            if (!auth?.token) return;
            setIsFetching(true);
            const res = await fetch('/api/superadmin/ai-entries', { headers: { Authorization: `Bearer ${auth.token}` } });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setEntries(data);
        } catch (error) {
            setFeedback({ message: `Error fetching entries: ${error.message}`, type: 'error' });
        } finally {
            setTimeout(() => setIsFetching(false), 500);
        }
    };

    useEffect(() => {
        fetchEntries();
    }, [auth]);

    const clearForm = () => {
        setEditMode(false);
        setIntent('');
        setBaseResponse('');
        setFeedback({ message: '', type: '' });
    };

    const handleEdit = (entry) => {
        setEditMode(true);
        setIntent(entry.intent);
        setBaseResponse(entry.responses.en);
        setFeedback({ message: '', type: '' });
        formRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleDelete = async (intentToDelete) => {
        if (!window.confirm(`Are you sure you want to delete the intent "${intentToDelete}"? This cannot be undone.`)) return;
        try {
            const res = await fetch(`/api/superadmin/ai-entries/${intentToDelete}`, { method: 'DELETE', headers: { Authorization: `Bearer ${auth.token}` } });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setFeedback({ message: data.message, type: 'success' });
            fetchEntries();
        } catch (error) {
            setFeedback({ message: `Error deleting entry: ${error.message}`, type: 'error' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFeedback({ message: '', type: '' });

        if (!auth?.token) {
            setFeedback({ message: 'Authentication error.', type: 'error' });
            setIsLoading(false);
            return;
        }

        try {
            const res = await fetch('/api/superadmin/train-ai', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.token}` },
                body: JSON.stringify({ intent, baseResponse }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            
            setFeedback({ message: data.message, type: 'success' });
            clearForm();
            fetchEntries();
        } catch (error) {
            setFeedback({ message: `Error: ${error.message}`, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="space-y-8">
            {/* Action Panel: Add/Edit Form */}
            <div ref={formRef} className={`bg-gray-800/50 border border-gray-700 rounded-lg p-6 transition-all duration-300 ${editMode ? 'border-cyan-500 ring-2 ring-cyan-500/20' : ''}`}>
                <h2 className="text-2xl font-bold text-white mb-2">{editMode ? 'Edit Knowledge Entry' : 'Add New Knowledge'}</h2>
                <p className="text-gray-400 mb-6">{editMode ? `You are currently editing the intent: ` : `Teach the AI new responses to user queries.`}<span className="font-mono text-cyan-400">{editMode && intent}</span></p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="intent" className="block text-sm font-medium text-gray-400 mb-1">Intent / Topic</label>
                        <input
                            type="text" id="intent" value={intent} onChange={(e) => setIntent(e.target.value)}
                            className="w-full bg-gray-900/50 border border-gray-600 rounded-md px-3 py-2 text-white font-mono focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all disabled:bg-gray-700 disabled:text-gray-500"
                            placeholder="e.g., pricing_model, contact_info" required disabled={editMode}
                        />
                    </div>
                    <div>
                        <label htmlFor="baseResponse" className="block text-sm font-medium text-gray-400 mb-1">Professional Response</label>
                        <textarea
                            id="baseResponse" value={baseResponse} onChange={(e) => setBaseResponse(e.target.value)} rows="6"
                            className="w-full bg-gray-900/50 border border-gray-600 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-all"
                            placeholder="Provide the complete, professional answer the AI should give..." required
                        />
                    </div>

                    {feedback.message && (
                        <div className={`p-3 rounded-md text-sm font-semibold flex items-center gap-2 ${feedback.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                            {feedback.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />} {feedback.message}
                        </div>
                    )}

                    <div className="flex items-center gap-4 pt-2">
                        <button type="submit" disabled={isLoading} className="flex items-center justify-center px-5 py-2 bg-cyan-600 text-white font-semibold rounded-lg hover:bg-cyan-700 disabled:bg-gray-600 transition-colors">
                            {isLoading ? <FaSpinner className="animate-spin mr-2" /> : (editMode ? <FaEdit className="mr-2" /> : <FaPlusCircle className="mr-2" />)}
                            {isLoading ? 'Processing...' : (editMode ? 'Update Knowledge' : 'Add Knowledge')}
                        </button>
                        {editMode && (
                            <button type="button" onClick={clearForm} className="px-5 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors">Cancel Edit</button>
                        )}
                    </div>
                </form>
            </div>

            {/* Data Panel: Current Knowledge Base */}
            <div>
                <h2 className="text-2xl font-bold text-white mb-4">Current Knowledge Base</h2>
                {isFetching ? <TableSkeleton /> : (
                    <div className="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="border-b border-gray-700">
                                    <tr>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Intent</th>
                                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Response Preview</th>
                                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700/50">
                                    {entries.length > 0 ? entries.map((entry) => (
                                        <tr key={entry.intent} className="hover:bg-gray-800/70 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-cyan-400">{entry.intent}</td>
                                            <td className="px-6 py-4 text-sm text-gray-400 max-w-md truncate">{entry.responses.en}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button onClick={() => handleEdit(entry)} className="p-2 text-gray-400 rounded-md hover:bg-blue-500/20 hover:text-blue-400 transition-all" aria-label={`Edit ${entry.intent}`}><FaEdit /></button>
                                                    <button onClick={() => handleDelete(entry.intent)} className="p-2 text-gray-400 rounded-md hover:bg-red-500/20 hover:text-red-400 transition-all" aria-label={`Delete ${entry.intent}`}><FaTrash /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="3" className="text-center py-10 text-gray-500">No knowledge entries found.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}